import type React from 'react';

import type { FireAnalyticsEvent } from '../../types';

// oxlint-disable typescript-eslint(consistent-type-imports)
// oxlint-disable-next-line typescript/consistent-type-imports
import { TASK_NAMES } from './task-names';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { TASK_NAMES } from './task-names';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { remapVitalStatsEventForQuickFind } from './remap-vital-stats-event-for-quick-find';

type MoreContext = Record<string, unknown>;

const baseVitalStatsEvent = ({
	searchSessionId,
	taskId,
	task,
	action,
	moreContext,
}: {
	searchSessionId: string;
	taskId: string;
	task: TaskNameValue;
	action: 'taskStart' | 'taskSuccess' | 'taskFail' | 'taskAbort';
	moreContext?: MoreContext;
}): FireAnalyticsEvent => {
	return {
		action,
		actionSubject: 'ui',
		eventType: 'operational' as const,
		attributes: {
			additionalAttributes: {
				task,
				taskId,
				hostname: window.location.hostname,
				searchSessionId,
				...moreContext,
			},
		},
	};
};

/**
 * State machine type for tracking network VitalStats task lifecycle.
 * Used across main.tsx, query-plan, search-results, and error components.
 *
 * State transitions:
 * - idle -> started (on taskStart)
 * - started -> completed (on taskSuccess)
 * - started -> failed (on taskFail)
 * - started -> aborted (on taskAbort - superseded or unmounted)
 */
export type NetworkTaskState = {
	taskId: string | null;
	uxTaskId: string | null;
	cacheAvailable: boolean;
	cacheExpected: boolean;
	state: 'idle' | 'started' | 'completed' | 'failed' | 'aborted';
};

export type FullPageSearchNetworkRefs = React.MutableRefObject<{
	taskId: string | null;
	startAt: number;
	state: NetworkTaskState;
}>;

/**
 * State machine type for tracking async search VitalStats task lifecycle.
 * Only active when cacheExpected=true (SSR/QF preload was attempted).
 *
 * Tracks whether aggregator served the response from its backend cache
 * (isCachedResult=true) after an async search query was made that expected a cached result.
 *
 * State transitions:
 * - idle -> started (on taskStart, only when cacheExpected=true)
 * - started -> completed (on taskSuccess, aggregator served from cache)
 * - started -> failed (on taskFail, aggregator did not serve from cache)
 * - started -> aborted (on taskAbort - superseded or unmounted)
 */
export type AsyncSearchTaskState = {
	taskId: string | null;
	uxTaskId: string | null;
	cacheExpected: boolean;
	state: 'idle' | 'started' | 'completed' | 'failed' | 'aborted';
};

export type FullPageSearchAsyncSearchRefs = React.MutableRefObject<{
	taskId: string | null;
	startAt: number;
	state: AsyncSearchTaskState;
}>;

type TaskNameValue = (typeof TASK_NAMES)[keyof typeof TASK_NAMES];

export const onTaskStart = ({
	searchSessionId,
	taskId,
	task,
	moreContext,
}: {
	searchSessionId: string;
	taskId: string;
	task: TaskNameValue;
	moreContext?: MoreContext;
}): FireAnalyticsEvent => {
	return baseVitalStatsEvent({ searchSessionId, taskId, task, action: 'taskStart', moreContext });
};

export const onTaskSuccess = ({
	searchSessionId,
	taskId,
	task,
	moreContext,
}: {
	searchSessionId: string;
	taskId: string;
	task: TaskNameValue;
	moreContext?: MoreContext;
}): FireAnalyticsEvent => {
	return baseVitalStatsEvent({ searchSessionId, taskId, task, action: 'taskSuccess', moreContext });
};

export const onTaskFail = ({
	searchSessionId,
	taskId,
	task,
	moreContext,
}: {
	searchSessionId: string;
	taskId: string;
	task: TaskNameValue;
	moreContext?: MoreContext;
}): FireAnalyticsEvent => {
	return baseVitalStatsEvent({ searchSessionId, taskId, task, action: 'taskFail', moreContext });
};

export const onTaskAbort = ({
	searchSessionId,
	taskId,
	task,
	moreContext,
}: {
	searchSessionId: string;
	taskId: string;
	task: TaskNameValue;
	moreContext?: MoreContext;
}): FireAnalyticsEvent => {
	return baseVitalStatsEvent({ searchSessionId, taskId, task, action: 'taskAbort', moreContext });
};
